import { Menu, MenuButton, MenuItems, Switch } from '@headlessui/react';
import {
  ChevronDownIcon,
  ClipboardDocumentIcon,
  GlobeAltIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toggleTemplateVisiblity } from '../clients/templateClient';

interface ShareTemplateButtonProps {
  templateId: number;
  isPublic: boolean | undefined;
  shareSlug: string | undefined;
}

export default function ShareTemplateButton({
  templateId,
  isPublic,
  shareSlug
}: ShareTemplateButtonProps) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  const toggleMutation = useMutation({
    mutationFn: (newStatus: boolean) => toggleTemplateVisiblity(templateId, newStatus),
    onSuccess: () => {
      // Invalidate and refetch template data
      queryClient.invalidateQueries({ queryKey: ['getTemplateAndBlocksById', templateId] });
    },
    onError: () => {
      // Error handling without toast
    }
  });

  const handleCopyLink = async () => {
    if (!shareSlug || !isPublic) return;

    const shareUrl = `${window.location.origin}/templates/preview/${shareSlug}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Error handling without toast
    }
  };

  const handleToggleVisibility = () => {
    toggleMutation.mutate(!isPublic);
  };

  const isPublicStatus = isPublic === true;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        className="flex items-center gap-2 px-3.5 py-2 rounded-lg shadow-lg text-sm font-semibold
          transition duration-300
          bg-white hover:bg-gray-50
          text-gray-700 border border-gray-300
          focus:outline-none"
      >
        {isPublicStatus ? (
          <>
            <GlobeAltIcon className="h-5 w-5 text-indigo-600" />
            <span>Public</span>
          </>
        ) : (
          <>
            <LockClosedIcon className="h-5 w-5 text-indigo-600" />
            <span>Private</span>
          </>
        )}
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      </MenuButton>

      <MenuItems
        transition
        className="absolute right-0 z-10 mt-2 w-80 origin-top-right rounded-md bg-white py-3 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        {/* Status line */}
        <div className="px-4 py-2 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {isPublicStatus ? (
              <>
                <GlobeAltIcon className="h-5 w-5 text-indigo-600" />
                <span className="text-sm font-medium text-gray-900">This template is public</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="h-5 w-5 text-indigo-600" />
                <span className="text-sm font-medium text-gray-900">This template is private</span>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-8 py-2 flex items-center gap-1">
          {/* Toggle switch */}
          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {isPublicStatus ? 'Public' : 'Private'}
            </span>
            <Switch
              checked={isPublicStatus}
              onChange={handleToggleVisibility}
              disabled={toggleMutation.isPending}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out
                focus:outline-none
                ${isPublicStatus ? 'bg-teal-500' : 'bg-gray-300'}
                ${toggleMutation.isPending ? 'opacity-50' : 'cursor-pointer'}
              `}
            >
              <span
                className={`
                  inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out
                  ${isPublicStatus ? 'translate-x-6' : 'translate-x-0.5'}
                `}
              />
            </Switch>
          </div>

          {/* Copy link button */}
          <button
            onClick={handleCopyLink}
            disabled={!isPublicStatus || !shareSlug || copied}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-md
              bg-indigo-600 hover:bg-indigo-700 text-white
              disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed
              transition-colors duration-200"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>

        {/* Helper text */}
        <div className="px-4 py-2 border-t border-gray-200">
          {isPublicStatus ? (
            <p className="text-xs text-gray-500">Anyone with the link can view this template.</p>
          ) : (
            <p className="text-xs text-gray-500">Template must be public to share via link.</p>
          )}
        </div>
      </MenuItems>
    </Menu>
  );
}
